"use client"

import * as React from "react"

/**
 * Global search-mode preference for filter popovers.
 *
 * "exact"  — word-boundary match. Typing "AI" finds "AI Officer" but not "Chair".
 * "fuzzy"  — subsequence match. Every character of the query must appear in
 *            the label in order; gaps allowed. "snrengmgr" → "Senior
 *            Engineering Manager".
 */
export type SearchMode = "exact" | "fuzzy"

const STORAGE_KEY = "recipient-filter-search-mode"
const DEFAULT_MODE: SearchMode = "exact"

// Module-level state shared across every hook subscriber
let currentMode: SearchMode | null = null
const listeners = new Set<() => void>()

function readFromStorage(): SearchMode {
  if (typeof window === "undefined") return DEFAULT_MODE
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return stored === "fuzzy" ? "fuzzy" : "exact"
  } catch {
    return DEFAULT_MODE
  }
}

function getSnapshot(): SearchMode {
  if (currentMode === null) currentMode = readFromStorage()
  return currentMode
}

function getServerSnapshot(): SearchMode {
  return DEFAULT_MODE
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

/**
 * Reads the current search mode and returns a setter that updates every
 * subscriber (i.e. every filter popover) at once.
 */
export function useSearchMode(): [SearchMode, (next: SearchMode) => void] {
  const mode = React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  const setMode = React.useCallback((next: SearchMode) => {
    if (next === currentMode) return
    currentMode = next
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(STORAGE_KEY, next)
      } catch {
        // localStorage blocked — fall back to in-memory
      }
    }
    listeners.forEach(cb => cb())
  }, [])

  return [mode, setMode]
}

/**
 * Fuzzy subsequence matcher: returns true if every character of `query`
 * appears in `label` in order, with any gaps allowed. Case-insensitive.
 */
export function fuzzyMatch(query: string, label: string): boolean {
  if (!query) return true
  const q = query.toLowerCase()
  const l = label.toLowerCase()
  let qi = 0
  for (let li = 0; li < l.length && qi < q.length; li++) {
    if (l[li] === q[qi]) qi++
  }
  return qi === q.length
}
